import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Chip,
  Card,
  CardContent,
  CardActions,
  IconButton,
  InputAdornment,
  Autocomplete,
  Divider,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import Search from '@mui/icons-material/Search';
import FilterList from '@mui/icons-material/FilterList';
import LunchDining from '@mui/icons-material/LunchDining';
import LocalPizza from '@mui/icons-material/LocalPizza';
import LocalDrink from '@mui/icons-material/LocalDrink';
import EmojiFoodBeverage from '@mui/icons-material/EmojiFoodBeverage';
import Icecream from '@mui/icons-material/Icecream';
import Fastfood from '@mui/icons-material/Fastfood';
import LocalCafe from '@mui/icons-material/LocalCafe';
import Cookie from '@mui/icons-material/Cookie';
import SportsBar from '@mui/icons-material/SportsBar';
import Coffee from '@mui/icons-material/Coffee';
import BakeryDining from '@mui/icons-material/BakeryDining';
import RamenDining from '@mui/icons-material/RamenDining';
import Egg from '@mui/icons-material/Egg';
import Check from '@mui/icons-material/Check';
import { Fee, CrudPermissions, ActivityPassPricing, Building, School, FeeType } from '../../types';

const CONCESSION_ICONS = [
  { name: 'fastfood', label: 'Fast Food', icon: Fastfood },
  { name: 'lunch', label: 'Burger/Hot Dog', icon: LunchDining },
  { name: 'pizza', label: 'Pizza', icon: LocalPizza },
  { name: 'drink', label: 'Drink', icon: LocalDrink },
  { name: 'coffee', label: 'Coffee', icon: Coffee },
  { name: 'beverage', label: 'Hot Beverage', icon: EmojiFoodBeverage },
  { name: 'icecream', label: 'Ice Cream', icon: Icecream },
  { name: 'cafe', label: 'Cafe', icon: LocalCafe },
  { name: 'cookie', label: 'Cookie/Snack', icon: Cookie },
  { name: 'beer', label: 'Beer/Soda', icon: SportsBar },
  { name: 'bakery', label: 'Bakery', icon: BakeryDining },
  { name: 'ramen', label: 'Noodles', icon: RamenDining },
  { name: 'egg', label: 'Breakfast', icon: Egg },
];
import { useData } from '../../contexts/DataContext';
import { getActiveBuildings } from '../../mock/buildings';
import { getActiveSchools } from '../../mock/schools';
import { mockFees } from '../../mock/fees';

interface FeesManagementProps {
  permissions: CrudPermissions;
}
import { v4 as uuidv4 } from 'uuid';

export const FeesManagement: React.FC<FeesManagementProps> = ({ permissions }) => {
  const { fees, setFees, feeCategories, courses, schoolYears, questionnaires, feeDisclaimers } = useData();
  const buildings = getActiveBuildings();
  const schools = getActiveSchools();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingFee, setEditingFee] = useState<Fee | null>(null);
  // The tour can mark a specific fee as the "showcase" so its edit
  // button gets a data-tour anchor for the cursor to land on. Reset
  // when the tour moves on.
  const [tourShowcaseFeeId, setTourShowcaseFeeId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [passTransactionFees, setPassTransactionFees] = useState(false);

  const TRANSACTION_FEE_PERCENT = 0.0345;
  const TRANSACTION_FEE_FLAT = 0.30;

  const calculateMarkup = (basePrice: number): number => {
    if (basePrice <= 0) return 0;
    return Math.ceil(((basePrice + TRANSACTION_FEE_FLAT) / (1 - TRANSACTION_FEE_PERCENT)) * 100) / 100;
  };
  const [formData, setFormData] = useState<Partial<Fee>>({
    name: '',
    description: '',
    categoryId: '6',
    course: '',
    buildingId: undefined,
    basePrice: 0,
    reducedPrice: 0,
    freePrice: 0,
    eventPricing: {
      studentChild: 0,
      adult: 0,
      senior: 0,
    },
    isActivityPass: false,
    activityPassPricing: {
      enabled: false,
      price: 0,
      isFree: false,
      quantity: 1,
    },
    availableFrom: new Date(),
    availableTo: new Date(),
    isActive: true,
    schoolId: '450e8400-e29b-41d4-a716-446655440000',
    schoolYearIds: [],
    requiresQuantity: false,
    maxQuantity: undefined,
    allowsPartialPayment: false,
    allowsCustomAmount: false,
    customAmountPresets: [],
    taxable: false,
    questionnaireId: undefined,
    disclaimerId: undefined,
    feeType: 'standard',
    popularityScore: 50,
    icon: undefined,
  });

  const filteredFees = useMemo(() => {
    return fees.filter((fee) => {
      const matchesSearch = fee.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        fee.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || fee.categoryId === categoryFilter;
      const matchesStatus = statusFilter === 'all' ||
        (statusFilter === 'active' && fee.isActive) ||
        (statusFilter === 'inactive' && !fee.isActive);
      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [fees, searchQuery, categoryFilter, statusFilter]);

  const getCategoryLabel = (categoryId: string) => {
    const category = feeCategories.find(c => c.id === categoryId);
    return category?.name || 'Unknown';
  };

  const handleAdd = () => {
    setEditingFee(null);
    setPassTransactionFees(false);
    setFormData({
      name: '',
      description: '',
      categoryId: '6',
      course: '',
      buildingId: undefined,
      basePrice: 0,
      reducedPrice: 0,
      freePrice: 0,
      eventPricing: {
        studentChild: 0,
        adult: 0,
        senior: 0,
      },
      isActivityPass: false,
      activityPassPricing: {
        enabled: false,
        price: 0,
        isFree: false,
        quantity: 1,
      },
      availableFrom: new Date(),
      availableTo: new Date(),
      isActive: true,
      schoolId: '450e8400-e29b-41d4-a716-446655440000',
      schoolYearIds: [],
      requiresQuantity: false,
      maxQuantity: undefined,
      allowsPartialPayment: false,
      allowsCustomAmount: false,
      customAmountPresets: [],
      taxable: false,
      questionnaireId: undefined,
      disclaimerId: undefined,
      feeType: 'standard',
      popularityScore: 50,
      icon: undefined,
    });
    setOpenDialog(true);
  };

  const handleEdit = (fee: Fee) => {
    setEditingFee(fee);
    setPassTransactionFees(false);
    setFormData({
      ...fee,
      eventPricing: fee.eventPricing || {
        studentChild: 0,
        adult: 0,
        senior: 0,
      },
      isActivityPass: fee.isActivityPass || false,
      activityPassPricing: fee.activityPassPricing || {
        enabled: false,
        price: 0,
        isFree: false,
        quantity: 1,
      },
      feeType: fee.feeType || 'standard',
      popularityScore: fee.popularityScore || 50,
      icon: fee.icon,
    });
    setOpenDialog(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this fee?')) {
      setFees(fees.filter((f) => f.id !== id));
      const index = mockFees.findIndex((f) => f.id === id);
      if (index > -1) {
        mockFees.splice(index, 1);
      }
    }
  };

  // Tour-driven auto-open + auto-close. The guided demo's markup step
  // needs the fee detail dialog visible so it can highlight the
  // transaction-fee toggle; subsequent steps need it gone again.
  // Track whether the dialog was opened by the tour so we know what to
  // close when the tour moves on.
  const handleEditRef = useRef(handleEdit);
  useEffect(() => { handleEditRef.current = handleEdit; });
  const tourOpenedRef = useRef(false);
  // Pick a fee with a real reduced/free discount so the tier-pricing
  // demo lands on differentiated numbers (the donation fee — first
  // match of basePrice ≥ 10 — has all three prices equal, which would
  // make the callout meaningless).
  const pickTieredShowcase = () =>
    fees.find((f) => f.reducedPrice > 0 && f.reducedPrice < f.basePrice) ??
    fees.find((f) => f.basePrice >= 10) ?? fees[0];

  const tourMarkedRef = useRef(false);
  useEffect(() => {
    const onTrigger = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.trigger === 'open-first-fee-detail') {
        const showcase = fees.find((f) => f.basePrice >= 10) ?? fees[0];
        if (showcase) {
          tourOpenedRef.current = true;
          handleEditRef.current(showcase);
        }
      } else if (detail?.trigger === 'mark-tier-fee-showcase') {
        // Tag the showcase fee's edit button so the cursor knows where
        // to land. Don't open the dialog yet — the tour wants the
        // listing visible for a beat before the click.
        const showcase = pickTieredShowcase();
        if (showcase) {
          tourMarkedRef.current = true;
          setTourShowcaseFeeId(showcase.id);
        }
      } else if (detail?.trigger === 'open-tiered-fee-detail') {
        const showcase = pickTieredShowcase();
        if (showcase) {
          tourOpenedRef.current = true;
          handleEditRef.current(showcase);
        }
      } else if (tourOpenedRef.current || tourMarkedRef.current) {
        // Step moved on — clean up everything the tour set up.
        tourOpenedRef.current = false;
        tourMarkedRef.current = false;
        setOpenDialog(false);
        setEditingFee(null);
        setTourShowcaseFeeId(null);
      }
    };
    window.addEventListener('cc-tour-trigger', onTrigger);
    return () => window.removeEventListener('cc-tour-trigger', onTrigger);
  }, [fees]);

  const handleSave = () => {
    if (editingFee) {
      const updated = fees.map((f) =>
        f.id === editingFee.id ? { ...f, ...formData } : f
      );
      setFees(updated);
      const index = mockFees.findIndex((f) => f.id === editingFee.id);
      if (index > -1) {
        mockFees[index] = { ...mockFees[index], ...formData } as Fee;
      }
    } else {
      const newFee: Fee = {
        id: uuidv4(),
        name: formData.name!,
        description: formData.description!,
        categoryId: formData.categoryId!,
        course: formData.course,
        buildingId: formData.buildingId,
        basePrice: formData.basePrice!,
        reducedPrice: formData.reducedPrice!,
        freePrice: formData.freePrice!,
        availableFrom: formData.availableFrom!,
        availableTo: formData.availableTo!,
        isActive: formData.isActive!,
        schoolId: '450e8400-e29b-41d4-a716-446655440000',
        schoolYearIds: formData.schoolYearIds || [],
        requiresQuantity: formData.requiresQuantity!,
        maxQuantity: formData.maxQuantity,
        allowsPartialPayment: formData.allowsPartialPayment,
        allowsCustomAmount: formData.allowsCustomAmount,
        customAmountPresets: formData.customAmountPresets,
        taxable: formData.taxable,
      };
      setFees([...fees, newFee]);
      mockFees.push(newFee);
    }
    setOpenDialog(false);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 3,
      }}>
        <Typography variant="h5" fontWeight={600}>
          Fees Management
        </Typography>
        {permissions.create && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAdd}
            sx={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              },
              flexShrink: 0,
            }}
          >
            Add Fee
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{
          display: 'flex',
          gap: 2,
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
        }}>
          <TextField
            placeholder="Search fees..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{
              flex: { xs: 'none', md: 1 },
              width: { xs: '100%', md: 'auto' },
              minWidth: { md: 250 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{
            display: 'flex',
            gap: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            width: { xs: '100%', md: 'auto' },
          }}>
            <FormControl size="small" sx={{ flex: { xs: 1, md: 'none' }, minWidth: { md: 180 } }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                label="Category"
              >
                <MenuItem value="all">All Categories</MenuItem>
                {feeCategories.filter(c => c.isActive).map(category => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: { xs: 1, md: 'none' }, minWidth: { md: 150 } }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
          },
          gap: 3,
        }}
      >
        {filteredFees.map((fee) => (
          <Box
            key={fee.id}
            sx={{
              display: 'flex',
              minWidth: 0,
            }}
          >
            <Card sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              overflow: 'hidden',
            }}>
              <CardContent sx={{
                flexGrow: 1,
                minWidth: 0,
                maxWidth: '100%',
                overflow: 'hidden',
              }}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'start',
                  mb: 2,
                  gap: 1,
                  minWidth: 0,
                  maxWidth: '100%',
                }}>
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{
                      flexGrow: 1,
                      minWidth: 0,
                      wordBreak: 'break-word',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {fee.name}
                  </Typography>
                  <Chip
                    label={fee.isActive ? 'Active' : 'Inactive'}
                    color={fee.isActive ? 'success' : 'default'}
                    size="small"
                    sx={{ flexShrink: 0 }}
                  />
                </Box>

                {fee.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      minWidth: 0,
                      maxWidth: '100%',
                      wordBreak: 'break-word',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {fee.description}
                  </Typography>
                )}

                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  minWidth: 0,
                  maxWidth: '100%',
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                      Category:
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{
                        textAlign: 'right',
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {getCategoryLabel(fee.categoryId)}
                    </Typography>
                  </Box>

                  {fee.course && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, minWidth: 0 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                        Course:
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{
                          textAlign: 'right',
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {fee.course}
                      </Typography>
                    </Box>
                  )}

                  {fee.isActivityPass && (
                    <Box sx={{ mb: 1 }}>
                      <Chip
                        label="Activity Pass"
                        size="small"
                        sx={{
                          bgcolor: '#fef3c7',
                          color: '#92400e',
                          fontWeight: 600,
                          border: '1px solid #fbbf24',
                        }}
                      />
                    </Box>
                  )}

                  {fee.feeType === 'concession' && (
                    <Box sx={{ mb: 1 }}>
                      <Chip
                        label="Concession Item"
                        size="small"
                        sx={{
                          bgcolor: '#fff7ed',
                          color: '#c2410c',
                          fontWeight: 600,
                          border: '1px solid #fb923c',
                        }}
                      />
                      {fee.popularityScore && (
                        <Chip
                          label={`Pop: ${fee.popularityScore}`}
                          size="small"
                          sx={{ ml: 0.5, bgcolor: '#f0fdf4', color: '#166534' }}
                        />
                      )}
                    </Box>
                  )}

                  {fee.activityPassPricing?.enabled && (
                    <Box sx={{ mb: 1, p: 1.5, bgcolor: '#f0fdf4', borderRadius: 1, border: '1px solid #bbf7d0' }}>
                      <Typography variant="caption" fontWeight={600} color="success.dark" sx={{ display: 'block', mb: 0.5 }}>
                        Activity Pass Pricing
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {fee.activityPassPricing.isFree ? 'FREE' : `$${fee.activityPassPricing.price.toFixed(2)}`}
                        {' '} (up to {fee.activityPassPricing.quantity})
                      </Typography>
                    </Box>
                  )}

                  {fee.categoryId === '5' && fee.eventPricing ? (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, minWidth: 0 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                          Student/Child:
                        </Typography>
                        <Typography variant="body2" fontWeight={600} color="primary" sx={{ flexShrink: 0 }}>
                          ${fee.eventPricing.studentChild.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, minWidth: 0 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                          Adult:
                        </Typography>
                        <Typography variant="body2" fontWeight={600} color="primary" sx={{ flexShrink: 0 }}>
                          ${fee.eventPricing.adult.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, minWidth: 0 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                          Senior:
                        </Typography>
                        <Typography variant="body2" fontWeight={600} color="primary" sx={{ flexShrink: 0 }}>
                          ${fee.eventPricing.senior.toFixed(2)}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, minWidth: 0 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                          Base Price:
                        </Typography>
                        <Typography variant="body2" fontWeight={600} color="primary" sx={{ flexShrink: 0 }}>
                          ${fee.basePrice.toFixed(2)}
                        </Typography>
                      </Box>

                      {fee.reducedPrice > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, minWidth: 0 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                            Reduced:
                          </Typography>
                          <Typography variant="body2" fontWeight={500} sx={{ flexShrink: 0 }}>
                            ${fee.reducedPrice.toFixed(2)}
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </CardContent>

              <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                {permissions.update && (
                  <Button
                    {...(fee.id === tourShowcaseFeeId
                      ? { 'data-tour': 'fee-edit-tour-target' }
                      : {})}
                    size="small"
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => handleEdit(fee)}
                    sx={{ flexGrow: 1, minWidth: 0 }}
                  >
                    Edit
                  </Button>
                )}
                {permissions.delete && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(fee.id)}
                    sx={{ flexShrink: 0 }}
                  >
                    <Delete />
                  </IconButton>
                )}
              </CardActions>
            </Card>
          </Box>
        ))}
      </Box>

      {filteredFees.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No fees found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {searchQuery || categoryFilter !== 'all' || statusFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'Click "Add Fee" to create your first fee'}
          </Typography>
        </Box>
      )}

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        sx={{
          // Tour-aware: when the bottom rail is showing (step 6), pad
          // the centring area so the dialog optically lifts above the
          // rail and the spotlighted pricing tiers stay visible.
          '& .MuiDialog-container': {
            paddingBottom: 'var(--tour-bottom-rail-height, 0px)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {editingFee ? 'Edit Fee' : 'Add New Fee'}
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '70vh', overflow: 'auto', p: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {/* Basic Information */}
            <Box sx={{ p: 3, bgcolor: 'white' }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: 'primary.main' }}>
                Basic Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Fee Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={2}
                  fullWidth
                />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={formData.categoryId}
                      onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                      label="Category"
                    >
                      {feeCategories.filter(c => c.isActive).map(category => (
                        <MenuItem key={category.id} value={category.id}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Fee Type</InputLabel>
                    <Select
                      value={formData.feeType || 'standard'}
                      onChange={(e) => setFormData({ ...formData, feeType: e.target.value as FeeType })}
                      label="Fee Type"
                    >
                      <MenuItem value="standard">Standard</MenuItem>
                      <MenuItem value="concession">Concession Item</MenuItem>
                      <MenuItem value="event_ticket">Event Ticket</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <Autocomplete
                    options={schools}
                    getOptionLabel={(option: School) => `${option.name} (${option.code})`}
                    value={schools.find(s => s.id === formData.schoolId) || null}
                    onChange={(_, newValue) => {
                      setFormData({ ...formData, schoolId: newValue?.id || '' });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="School"
                        required
                        placeholder="Select school..."
                      />
                    )}
                    fullWidth
                  />
                  {(formData.feeType === 'concession' || formData.feeType === 'event_ticket') && (
                    <TextField
                      label="Popularity Score"
                      type="number"
                      value={formData.popularityScore || 50}
                      onChange={(e) => setFormData({ ...formData, popularityScore: parseInt(e.target.value) || 50 })}
                      inputProps={{ min: 0, max: 100 }}
                      helperText="Higher scores appear first in POS (0-100)"
                      fullWidth
                    />
                  )}
                </Box>
                {formData.feeType === 'concession' && (
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                      POS Display Icon
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                        gap: 1,
                      }}
                    >
                      {CONCESSION_ICONS.map((iconOption) => {
                        const IconComponent = iconOption.icon;
                        const isSelected = formData.icon === iconOption.name;
                        return (
                          <Paper
                            key={iconOption.name}
                            onClick={() => setFormData({ ...formData, icon: iconOption.name })}
                            sx={{
                              p: 1.5,
                              cursor: 'pointer',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 0.5,
                              border: '2px solid',
                              borderColor: isSelected ? 'primary.main' : 'transparent',
                              bgcolor: isSelected ? 'primary.50' : 'grey.50',
                              borderRadius: 2,
                              transition: 'all 0.15s ease',
                              position: 'relative',
                              '&:hover': {
                                bgcolor: isSelected ? 'primary.50' : 'grey.100',
                                transform: 'scale(1.02)',
                              },
                            }}
                          >
                            {isSelected && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                  width: 16,
                                  height: 16,
                                  borderRadius: '50%',
                                  bgcolor: 'primary.main',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Check sx={{ fontSize: 12, color: 'white' }} />
                              </Box>
                            )}
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 1.5,
                                bgcolor: isSelected ? '#fef3c7' : 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: isSelected ? '#f59e0b' : 'grey.600',
                              }}
                            >
                              <IconComponent />
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.65rem',
                                textAlign: 'center',
                                color: isSelected ? 'primary.main' : 'text.secondary',
                                fontWeight: isSelected ? 600 : 400,
                              }}
                            >
                              {iconOption.label}
                            </Typography>
                          </Paper>
                        );
                      })}
                    </Box>
                  </Box>
                )}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <Autocomplete
                    options={buildings}
                    getOptionLabel={(option: Building) => `${option.name} (${option.code})`}
                    value={buildings.find(b => b.id === formData.buildingId) || null}
                    onChange={(_, newValue) => {
                      setFormData({ ...formData, buildingId: newValue?.id });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Building (optional)"
                        placeholder="Select building..."
                      />
                    )}
                    fullWidth
                  />
                  <Autocomplete
                    options={courses.filter(c => c.isActive)}
                    getOptionLabel={(option) => `${option.name} (${option.code})`}
                    value={courses.find(c => c.name === formData.course) || null}
                    onChange={(_, newValue) => {
                      setFormData({ ...formData, course: newValue?.name || '' });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Course (optional)"
                        placeholder="Search courses..."
                      />
                    )}
                    fullWidth
                  />
                </Box>
              </Box>
            </Box>

            <Divider />

            {/* Pricing */}
            <Box sx={{ p: 3, bgcolor: '#fafafa' }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: 'primary.main' }}>
                Pricing
              </Typography>
              {formData.feeType === 'event_ticket' ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                  <TextField
                    label="Student/Child Price"
                    type="number"
                    value={formData.eventPricing?.studentChild || 0}
                    onChange={(e) => setFormData({
                      ...formData,
                      eventPricing: {
                        ...formData.eventPricing!,
                        studentChild: parseFloat(e.target.value) || 0,
                      },
                    })}
                    inputProps={{ min: 0, step: 0.01 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                  <TextField
                    label="Adult Price"
                    type="number"
                    value={formData.eventPricing?.adult || 0}
                    onChange={(e) => setFormData({
                      ...formData,
                      eventPricing: {
                        ...formData.eventPricing!,
                        adult: parseFloat(e.target.value) || 0,
                      },
                    })}
                    inputProps={{ min: 0, step: 0.01 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                  <TextField
                    label="Senior Price"
                    type="number"
                    value={formData.eventPricing?.senior || 0}
                    onChange={(e) => setFormData({
                      ...formData,
                      eventPricing: {
                        ...formData.eventPricing!,
                        senior: parseFloat(e.target.value) || 0,
                      },
                    })}
                    inputProps={{ min: 0, step: 0.01 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Box>
              ) : (
                <Box data-tour="fee-pricing-tiers" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                  <TextField
                    label="Base Price"
                    type="number"
                    value={formData.basePrice}
                    onChange={(e) => setFormData({ ...formData, basePrice: parseFloat(e.target.value) })}
                    inputProps={{ min: 0, step: 0.01 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                  <TextField
                    label="Reduced Price"
                    type="number"
                    value={formData.reducedPrice}
                    onChange={(e) => setFormData({ ...formData, reducedPrice: parseFloat(e.target.value) })}
                    inputProps={{ min: 0, step: 0.01 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                  <TextField
                    label="Free Price"
                    type="number"
                    value={formData.freePrice}
                    onChange={(e) => setFormData({ ...formData, freePrice: parseFloat(e.target.value) })}
                    inputProps={{ min: 0, step: 0.01 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Box>
              )}

              <Box data-tour="fee-markup-toggle" sx={{ mt: 3, p: 2, bgcolor: 'white', borderRadius: 2, border: '1px solid #e5e7eb' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={passTransactionFees}
                      onChange={(e) => setPassTransactionFees(e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight={500}>Pass transaction fees to payer</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Add 3.45% + $0.30 per transaction to cover payment processing costs
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', m: 0 }}
                />

                {passTransactionFees && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: '#f0fdf4', borderRadius: 1.5, border: '1px solid #bbf7d0' }}>
                    <Typography variant="subtitle2" fontWeight={600} color="success.dark" sx={{ mb: 1.5 }}>
                      Final Prices with Transaction Fees
                    </Typography>
                    {formData.feeType === 'event_ticket' ? (
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                        {(formData.eventPricing?.studentChild || 0) > 0 && (
                          <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Student/Child
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                              ${(formData.eventPricing?.studentChild || 0).toFixed(2)}
                            </Typography>
                            <Typography variant="h6" fontWeight={700} color="success.main">
                              ${calculateMarkup(formData.eventPricing?.studentChild || 0).toFixed(2)}
                            </Typography>
                          </Box>
                        )}
                        {(formData.eventPricing?.adult || 0) > 0 && (
                          <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Adult
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                              ${(formData.eventPricing?.adult || 0).toFixed(2)}
                            </Typography>
                            <Typography variant="h6" fontWeight={700} color="success.main">
                              ${calculateMarkup(formData.eventPricing?.adult || 0).toFixed(2)}
                            </Typography>
                          </Box>
                        )}
                        {(formData.eventPricing?.senior || 0) > 0 && (
                          <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Senior
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                              ${(formData.eventPricing?.senior || 0).toFixed(2)}
                            </Typography>
                            <Typography variant="h6" fontWeight={700} color="success.main">
                              ${calculateMarkup(formData.eventPricing?.senior || 0).toFixed(2)}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                        {(formData.basePrice || 0) > 0 && (
                          <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Base Price
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                              ${(formData.basePrice || 0).toFixed(2)}
                            </Typography>
                            <Typography variant="h6" fontWeight={700} color="success.main">
                              ${calculateMarkup(formData.basePrice || 0).toFixed(2)}
                            </Typography>
                          </Box>
                        )}
                        {(formData.reducedPrice || 0) > 0 && (
                          <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Reduced Price
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                              ${(formData.reducedPrice || 0).toFixed(2)}
                            </Typography>
                            <Typography variant="h6" fontWeight={700} color="success.main">
                              ${calculateMarkup(formData.reducedPrice || 0).toFixed(2)}
                            </Typography>
                          </Box>
                        )}
                        {(formData.freePrice || 0) > 0 && (
                          <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Free Price
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                              ${(formData.freePrice || 0).toFixed(2)}
                            </Typography>
                            <Typography variant="h6" fontWeight={700} color="success.main">
                              ${calculateMarkup(formData.freePrice || 0).toFixed(2)}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, fontStyle: 'italic' }}>
                      These are the amounts that will be charged. You will receive your original amounts after fees.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Activity Pass Settings - Highlighted Section for Events */}
            {formData.feeType === 'event_ticket' && (
              <>
                <Divider />
                <Box sx={{ p: 3, bgcolor: formData.isActivityPass || formData.activityPassPricing?.enabled ? '#fffbeb' : 'white', border: formData.isActivityPass || formData.activityPassPricing?.enabled ? '2px solid #fbbf24' : 'none', borderLeft: 'none', borderRight: 'none' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography sx={{ fontSize: 18 }}>🎫</Typography>
                    </Box>
                    <Typography variant="subtitle1" fontWeight={600} color="#92400e">
                      Activity Pass Settings
                    </Typography>
                  </Box>

                  <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, border: '1px solid #e5e7eb' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.isActivityPass}
                          onChange={(e) => setFormData({ ...formData, isActivityPass: e.target.checked })}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight={500}>This is a Student Activity Pass</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Mark this if this fee IS the activity pass that unlocks discounts on other events
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: 'flex-start', m: 0 }}
                    />
                  </Box>

                  {!formData.isActivityPass && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: 2, border: '1px solid #e5e7eb' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.activityPassPricing?.enabled || false}
                            onChange={(e) => setFormData({
                              ...formData,
                              activityPassPricing: {
                                ...formData.activityPassPricing!,
                                enabled: e.target.checked,
                              },
                            })}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body1" fontWeight={500}>Enable Activity Pass Discount</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Students with an activity pass get a special price on this event
                            </Typography>
                          </Box>
                        }
                        sx={{ alignItems: 'flex-start', m: 0, mb: formData.activityPassPricing?.enabled ? 2 : 0 }}
                      />

                      {formData.activityPassPricing?.enabled && (
                        <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed #e5e7eb' }}>
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                            <Box sx={{ p: 2, bgcolor: formData.activityPassPricing?.isFree ? '#f0fdf4' : '#f9fafb', borderRadius: 1.5, border: '1px solid', borderColor: formData.activityPassPricing?.isFree ? '#bbf7d0' : '#e5e7eb' }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={formData.activityPassPricing?.isFree || false}
                                    onChange={(e) => setFormData({
                                      ...formData,
                                      activityPassPricing: {
                                        ...formData.activityPassPricing!,
                                        isFree: e.target.checked,
                                        price: e.target.checked ? 0 : formData.activityPassPricing!.price,
                                      },
                                    })}
                                  />
                                }
                                label={
                                  <Box>
                                    <Typography variant="body2" fontWeight={600} color={formData.activityPassPricing?.isFree ? 'success.main' : 'text.primary'}>
                                      FREE with Activity Pass
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      No charge for pass holders
                                    </Typography>
                                  </Box>
                                }
                                sx={{ m: 0 }}
                              />
                            </Box>

                            {!formData.activityPassPricing?.isFree && (
                              <Box>
                                <TextField
                                  label="Activity Pass Price"
                                  type="number"
                                  value={formData.activityPassPricing?.price || 0}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    activityPassPricing: {
                                      ...formData.activityPassPricing!,
                                      price: parseFloat(e.target.value) || 0,
                                    },
                                  })}
                                  inputProps={{ min: 0, step: 0.01 }}
                                  InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                  }}
                                  helperText="Discounted price for activity pass holders"
                                  fullWidth
                                />
                                {passTransactionFees && (formData.activityPassPricing?.price || 0) > 0 && (
                                  <Box sx={{ mt: 1, p: 1, bgcolor: '#f0fdf4', borderRadius: 1, textAlign: 'center' }}>
                                    <Typography variant="caption" color="text.secondary">
                                      With fees:{' '}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={700} color="success.main" component="span">
                                      ${calculateMarkup(formData.activityPassPricing?.price || 0).toFixed(2)}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Box>

                          <TextField
                            label="Quantity at Activity Pass Price"
                            type="number"
                            value={formData.activityPassPricing?.quantity || 1}
                            onChange={(e) => setFormData({
                              ...formData,
                              activityPassPricing: {
                                ...formData.activityPassPricing!,
                                quantity: parseInt(e.target.value) || 1,
                              },
                            })}
                            inputProps={{ min: 1 }}
                            helperText="How many tickets can be purchased at the activity pass price before regular pricing applies"
                            fullWidth
                          />
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </>
            )}

            <Divider />

            {/* Availability */}
            <Box sx={{ p: 3, bgcolor: 'white' }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: 'primary.main' }}>
                Availability
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <TextField
                    label="Available From"
                    type="date"
                    value={formData.availableFrom instanceof Date ? formData.availableFrom.toISOString().split('T')[0] : ''}
                    onChange={(e) => setFormData({ ...formData, availableFrom: new Date(e.target.value) })}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Available To"
                    type="date"
                    value={formData.availableTo instanceof Date ? formData.availableTo.toISOString().split('T')[0] : ''}
                    onChange={(e) => setFormData({ ...formData, availableTo: new Date(e.target.value) })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
                <Autocomplete
                  multiple
                  options={schoolYears.filter(y => y.isActive)}
                  getOptionLabel={(option) => option.name}
                  value={schoolYears.filter(y => formData.schoolYearIds?.includes(y.id))}
                  onChange={(_, newValue) => {
                    setFormData({ ...formData, schoolYearIds: newValue.map(y => y.id) });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="School Years"
                      placeholder="Select school years..."
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={key}
                          label={option.name}
                          {...tagProps}
                          size="small"
                        />
                      );
                    })
                  }
                  fullWidth
                />
              </Box>
            </Box>

            <Divider />

            {/* Additional Options */}
            <Box sx={{ p: 3, bgcolor: '#fafafa' }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: 'primary.main' }}>
                Additional Options
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Autocomplete
                  options={questionnaires}
                  getOptionLabel={(option) => option.name}
                  value={questionnaires.find(q => q.id === formData.questionnaireId) || null}
                  onChange={(_, newValue) => {
                    setFormData({ ...formData, questionnaireId: newValue?.id });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Questionnaire (optional)"
                      placeholder="Select a questionnaire..."
                    />
                  )}
                  fullWidth
                />
                <Autocomplete
                  options={feeDisclaimers}
                  getOptionLabel={(option) => option.name}
                  value={feeDisclaimers.find(d => d.id === formData.disclaimerId) || null}
                  onChange={(_, newValue) => {
                    setFormData({ ...formData, disclaimerId: newValue?.id });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Disclaimer (optional)"
                      placeholder="Select a disclaimer..."
                    />
                  )}
                  fullWidth
                />
              </Box>

              <Box sx={{ mt: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.allowsPartialPayment}
                      onChange={(e) => setFormData({ ...formData, allowsPartialPayment: e.target.checked })}
                    />
                  }
                  label="Allows Partial Payment"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.requiresQuantity}
                      onChange={(e) => setFormData({ ...formData, requiresQuantity: e.target.checked })}
                    />
                  }
                  label="Requires Quantity"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.allowsCustomAmount}
                      onChange={(e) => setFormData({ ...formData, allowsCustomAmount: e.target.checked })}
                    />
                  }
                  label="Allows Custom Amount (donations)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!formData.taxable}
                      onChange={(e) => setFormData({ ...formData, taxable: e.target.checked })}
                    />
                  }
                  label="Taxable (apply school tax rate)"
                />
              </Box>

              {formData.requiresQuantity && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    label="Max Quantity (optional)"
                    type="number"
                    value={formData.maxQuantity || ''}
                    onChange={(e) => setFormData({ ...formData, maxQuantity: e.target.value ? parseInt(e.target.value) : undefined })}
                    inputProps={{ min: 1 }}
                    sx={{ maxWidth: 200 }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
